import { useEffect, useState, type FormEvent } from "react";
import {
  authConfigured,
  completeNewPassword,
  signIn,
  type NewPasswordChallenge,
} from "../auth";
import { Icon } from "../icons";
import type { AuthSession } from "../types";

export function AuthModal({
  open,
  onClose,
  onAuthenticated,
}: {
  open: boolean;
  onClose: () => void;
  onAuthenticated: (session: AuthSession) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [challenge, setChallenge] = useState<NewPasswordChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPassword("");
      setNewPassword("");
      setChallenge(null);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (challenge) {
        const session = await completeNewPassword(challenge, newPassword);
        onAuthenticated(session);
        onClose();
      } else {
        const result = await signIn(email, password);
        if (result.kind === "NEW_PASSWORD_REQUIRED") {
          setChallenge(result);
          setPassword("");
        } else {
          onAuthenticated(result.session);
          onClose();
        }
      }
    } catch (cause) {
      setError(authMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="modal auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="modal-close" onClick={onClose} type="button" aria-label="Cerrar">
          <Icon name="x" size={19} />
        </button>
        <div className="auth-brand">
          <span><Icon name="chat" size={21} /></span>
          <div><strong>Conversa Smart</strong><small>Acceso del equipo</small></div>
        </div>
        <p className="section-kicker">ESPACIO PRIVADO</p>
        <h2 id="auth-title">{challenge ? "Define tu contraseña" : "Ingresa a tu espacio de trabajo"}</h2>
        <p className="modal-description">
          {challenge
            ? "Cognito exige cambiar la contraseña temporal antes del primer ingreso."
            : "Usa el correo corporativo invitado por el administrador. El modo demostración permanece disponible sin iniciar sesión."}
        </p>

        {!authConfigured() ? (
          <div className="notice warning"><strong>Autenticación aún no configurada.</strong><span>El entorno no recibió los identificadores de Cognito.</span></div>
        ) : (
          <form className="auth-form" onSubmit={submit}>
            {!challenge ? (
              <>
                <label>
                  <span>Correo corporativo</span>
                  <input autoComplete="username" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="nombre@empresa.cl" required />
                </label>
                <label>
                  <span>Contraseña</span>
                  <input autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required />
                </label>
              </>
            ) : (
              <label>
                <span>Nueva contraseña</span>
                <input autoComplete="new-password" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={10} required />
                <small>Al menos 10 caracteres, mayúscula, minúscula y número.</small>
              </label>
            )}
            {error ? <div className="form-error">{error}</div> : null}
            <button className="button primary wide" disabled={busy} type="submit">
              {busy ? "Validando…" : challenge ? "Guardar y entrar" : "Ingresar"}
              {!busy ? <Icon name="arrow" size={17} /> : null}
            </button>
          </form>
        )}
        <div className="auth-help">
          <Icon name="check" size={17} />
          <span>Las conversaciones se separan por empresa y se audita cada cambio.</span>
        </div>
      </section>
    </div>
  );
}

function authMessage(cause: unknown) {
  const code = cause instanceof Error ? cause.message : "AUTHENTICATION_FAILED";
  if (/NotAuthorizedException|Incorrect username or password/i.test(code)) return "Correo o contraseña incorrectos.";
  if (/UserNotFoundException/i.test(code)) return "El usuario no existe o todavía no fue invitado.";
  if (/PasswordResetRequiredException/i.test(code)) return "Debes restablecer la contraseña desde la invitación recibida.";
  if (/TooManyRequestsException/i.test(code)) return "Demasiados intentos. Espera unos minutos antes de volver a probar.";
  if (/AUTH_NOT_CONFIGURED/i.test(code)) return "La conexión con Cognito no está configurada en este entorno.";
  return code;
}
